import { useAppSelector } from 'redux/store';
import { FEATURES, USER_ROLES, WORKFLOWS } from './types';

const workflowsToRoles: Record<WORKFLOWS, USER_ROLES[]> = {
  [WORKFLOWS.ANTICIPATION]: [USER_ROLES.PUBLICATION_TRACKER],
  [WORKFLOWS.INFRINGEMENT_DETECTION]: [USER_ROLES.INFRINGEMENT_DETECTION],
  [WORKFLOWS.INVENTION_DISCLOSURE]: [USER_ROLES.INVENTION_DISCLOSURE],
  [WORKFLOWS.FTO]: [USER_ROLES.FTO],
  [WORKFLOWS.PATENT_DRAFT]: [USER_ROLES.PATENT_DRAFT, USER_ROLES.CHEMICAL_DRAFT],
  [WORKFLOWS.DISCLOSURE_DRAFT]: [USER_ROLES.DISCLOSURE_DRAFT],
  [WORKFLOWS.AI_SEARCH]: [USER_ROLES.AI_SEARCH],
  [WORKFLOWS.CONCEPT_SEARCH]: [USER_ROLES.AI_SEARCH_QUERY],
  [WORKFLOWS.ORG_MANAGEMENT]: [USER_ROLES.ORG_ADMIN, USER_ROLES.PLATFORM_ADMIN],
  [WORKFLOWS.PLATFORM_MANAGEMENT]: [USER_ROLES.PLATFORM_ADMIN],
  [WORKFLOWS.FEATURES_FLAGS_VIEW]: [USER_ROLES.PLATFORM_ADMIN, USER_ROLES.DEVELOPER],
  [WORKFLOWS.DEVELOPER_MANAGEMENT]: [USER_ROLES.DEVELOPER_ADMIN],
  [WORKFLOWS.DEVELOPER]: [USER_ROLES.DEVELOPER],
  [WORKFLOWS.OFFICE_ACTIONS]: [USER_ROLES.OFFICE_ACTION],
  [WORKFLOWS.PATENT_INTELLIGENCE]: [USER_ROLES.MILETUS],
  [WORKFLOWS.LITIGATION_ASSISTANT]: [USER_ROLES.LITIGATION_ASSISTANT],
  [WORKFLOWS.CONFERENCE_MANAGEMENT]: [USER_ROLES.CONFERENCE_ADMIN],
  [WORKFLOWS.GENERAL_ASSISTANT]: [USER_ROLES.GENERAL_ASSISTANT],
  [WORKFLOWS.INVENTION_ASSISTANT]: [USER_ROLES.INVENTION_ASSISTANT],
};

const featuresToRoles: Record<FEATURES, USER_ROLES[]> = {
  [FEATURES.IMAGE_GENERATION]: [USER_ROLES.IMAGE_GENERATION],
  [FEATURES.IMAGE_GENERATION_V2]: [USER_ROLES.IMAGE_GENERATION_V2],
  [FEATURES.CHEMISTRY]: [USER_ROLES.CHEMISTRY],
  [FEATURES.SEQUENCES]: [USER_ROLES.SEQUENCES],
  [FEATURES.DICTATION]: [USER_ROLES.DICTATION],
  [FEATURES.PATENT_DRAFTING_MECHANICAL_MODE]: [USER_ROLES.PATENT_DRAFT],
  [FEATURES.PATENT_DRAFTING_CHEMICAL_MODE]: [USER_ROLES.CHEMICAL_DRAFT],
  [FEATURES.FORCE_ACTIVE_KNOWLEDGE]: [USER_ROLES.KNOWLEDGE_ADMIN],
};

export const useRolesCheck = () => {
  'use memo';

  const { role } = useAppSelector(store => store.authSlice);

  const canAccessWorkflow = (workflow: WORKFLOWS) => {
    const roles = workflowsToRoles[workflow];

    return role && (roles.some(allowedRole => role?.includes(allowedRole)) || roles.length === 0);
  };

  const canAccessFeature = (feature: FEATURES) => {
    const roles = featuresToRoles[feature];

    return role && (roles.some(allowedRole => role?.includes(allowedRole)) || roles.length === 0);
  };

  const hasRole = (userRole: string) => Boolean(role?.some(granted => String(granted) === userRole));

  const isDeveloper = role?.includes(USER_ROLES.DEVELOPER);
  const canCopyIds = isDeveloper || role?.includes(USER_ROLES.PLATFORM_ADMIN);
  const isOrgAdmin = !!role?.includes(USER_ROLES.ORG_ADMIN);

  return {
    canAccessWorkflow,
    canAccessFeature,
    hasRole,
    isDeveloper,
    canCopyIds,
    isOrgAdmin,
  };
};
